<template>
	<!-- The detail page of the workbench, rendered as a pane body inside the
	     shared tool rail: the rail's header already prints the title and the
	     rail's body already pads and scrolls, so this is only the reading
	     surface itself — the record the ledger, the map or another pane named.
	     Authoring is not here: both create forms are tools on the right rail, so
	     this pane never stops being a reader. -->
	<div>
		<WorkDetails
			v-if="selectedWorkRows.length > 0"
			:work="selectedWorkRows"
			@clear="emit('close')"
			@create-route="emit('create-route')"
			@create-assignments="emit('create-assignments')"
		/>

		<AssignmentSelection
			v-else-if="selectedAssignments.length > 0"
			:assignments="selectedAssignments"
			:users="users"
			:sites="sites"
			@clear="emit('close')"
			@create-route="emit('route-from-assignments', $event)"
		/>

		<AssignmentDetails
			v-else-if="selectedAssignment"
			:assignment="selectedAssignment"
			:users="users"
			:sites="sites"
			:trap-configurations="trapConfigurations"
			:routes="routes"
			@notify="forwardNotify"
			@changed="emit('changed')"
			@select="emit('select', $event)"
		/>

		<RouteDetails
			v-else-if="selectedRoute"
			:route="selectedRoute"
			:users="users"
			:sites="sites"
			:assignments="assignments"
			:plan="planForRoute"
			@notify="forwardNotify"
			@changed="emit('changed')"
			@select="emit('select', $event)"
		/>

		<TrapDetails
			v-else-if="selectedTrap"
			:trap="selectedTrap"
			@collect="onCollect"
		/>

		<StationDetails
			v-else-if="selectedStation"
			:station-id="selectedStation.id"
			@select="emit('select', $event)"
			@notify="forwardNotify"
			@set-trap="onSetTrap"
		/>

		<TechnicianDetails
			v-else-if="selectedTechnician"
			:technician="selectedTechnician"
			:assignments="assignments"
			:routes="routes"
			:sites="sites"
			@select="emit('select', $event)"
			@assign-work="onAssignWork"
		/>

		<!-- Two different silences: nothing chosen yet is the page waiting for
		     the operator, while a selection whose row the last reload dropped is
		     a stale name the collection can no longer look up. Saying which one
		     it is keeps an empty panel from reading as a broken one. -->
		<p v-else-if="selection" class="text-body-secondary small mb-0">
			This selection is no longer loaded. Close the panel or choose another
			entry.
		</p>
		<p v-else class="text-body-secondary small mb-0">
			Select an assignment, route, technician, station or trap to read its
			details.
		</p>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type {
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	MapReviewTrapStationRow,
	OperationsWorkRow,
	RoutePlan,
	SyncAssignment,
	SyncRoute,
	TrapConfigurationResult,
	User,
} from "@/type/api";

import AssignmentDetails from "./AssignmentDetails.vue";
import AssignmentSelection from "./AssignmentSelection.vue";
import RouteDetails from "./RouteDetails.vue";
import StationDetails from "./StationDetails.vue";
import TechnicianDetails from "./TechnicianDetails.vue";
import TrapDetails from "./TrapDetails.vue";
import WorkDetails from "./WorkDetails.vue";
import type { Selection } from "./model";

const props = defineProps<{
	selection: Selection | null;
	users: User[];
	sites: MapReviewSiteRow[];
	trapConfigurations: TrapConfigurationResult[];
	assignments: SyncAssignment[];
	routes: SyncRoute[];
	stations: MapReviewTrapStationRow[];
	traps: MapReviewTrapInstanceRow[];
	work: OperationsWorkRow[];
	/** A work order fetched on demand for a selection the Work queue does not
	 * carry — a footprint clicked on the map, which the queue may have no
	 * dispatchable row for. Null until one is read, and for every other kind. */
	workDetailRow?: OperationsWorkRow | null;
	selectedKeys: Set<string>;
	/** The day board's plans by route id: a pane states the plan of the record it
	 * shows from the page's one read, so a card never fetches its own. */
	plans: Map<number, RoutePlan>;
}>();

const emit = defineEmits<{
	select: [selection: Selection];
	close: [];
	notify: [title: string, message: string];
	changed: [];
	collect: [trap: MapReviewTrapInstanceRow];
	"assign-work": [technicianId: number];
	"set-trap": [siteId: number];
	"create-route": [];
	"create-assignments": [];
	"route-from-assignments": [assignmentIds: number[]];
}>();

const WORK_KINDS = new Set(["work_lead", "work_order", "work_direct"]);

function workRowKind(origin: string): string {
	return origin === "lead"
		? "work_lead"
		: origin === "work_order"
			? "work_order"
			: "work_direct";
}

/** The Work queue plus the one work order read on demand. The map draws every
 * open footprint while the queue lists the work an office dispatches, so a
 * clicked footprint can name a row only this pane knows about; it joins the pool
 * the selection is filtered from, and a queue row of the same kind and id wins
 * so one work order is never listed twice. */
const workPool = computed<OperationsWorkRow[]>(() => {
	const detail = props.workDetailRow;
	if (!detail) return props.work;
	const carried = props.work.some(
		(row) =>
			workRowKind(row.origin) === workRowKind(detail.origin) &&
			row.id === detail.id,
	);
	return carried ? props.work : [detail, ...props.work];
});

const selectedWorkRows = computed(() => {
	if (!props.selection || !WORK_KINDS.has(props.selection.kind)) return [];
	const selected = workPool.value.filter((row) =>
		props.selectedKeys.has(`${workRowKind(row.origin)}:${row.id}`),
	);
	if (selected.length > 0) return selected;
	return workPool.value.filter(
		(row) =>
			workRowKind(row.origin) === props.selection?.kind &&
			row.id === props.selection?.id,
	);
});

const selectedAssignment = computed(() =>
	props.selection?.kind === "assignment"
		? props.assignments.find((row) => row.id === props.selection?.id)
		: undefined,
);

/** The checked assignments, when the ledger's checkboxes hold more than one: a
 * multi-selection is read as a list, while a single row keeps its full details.
 * The checked set is what the checkboxes say — the record the last check
 * selected is one of them, not the whole answer. */
const selectedAssignments = computed<SyncAssignment[]>(() => {
	if (props.selection?.kind !== "assignment") return [];
	const checked = props.assignments.filter((row) =>
		props.selectedKeys.has(`assignment:${row.id}`),
	);
	return checked.length > 1 ? checked : [];
});

const selectedRoute = computed(() =>
	props.selection?.kind === "route"
		? props.routes.find((row) => row.id === props.selection?.id)
		: undefined,
);

/** The selected Route's plan, or null while the page holds none for it — a
 * Route outside the picked day, or one the board has not answered for yet. The
 * pane says "no plan yet" rather than falling back to another Route's plan. */
const planForRoute = computed<RoutePlan | null>(() =>
	selectedRoute.value
		? (props.plans.get(selectedRoute.value.id) ?? null)
		: null,
);

const selectedTrap = computed(() =>
	props.selection?.kind === "trap"
		? props.traps.find((row) => row.id === props.selection?.id)
		: undefined,
);

const selectedStation = computed(() =>
	props.selection?.kind === "station"
		? props.stations.find((row) => row.id === props.selection?.id)
		: undefined,
);

const selectedTechnician = computed(() =>
	props.selection?.kind === "technician"
		? props.users.find((row) => row.id === props.selection?.id)
		: undefined,
);

function forwardNotify(title: string, message: string) {
	emit("notify", title, message);
}

function onCollect(trap: MapReviewTrapInstanceRow) {
	emit("collect", trap);
}

function onAssignWork(technicianId: number) {
	emit("assign-work", technicianId);
}

function onSetTrap(siteId: number) {
	emit("set-trap", siteId);
}
</script>
